// ── UpcomingOpportunitiesSection ✦ — toggle-aware, calm cards ───
//
// Constitutional rules:
//   • Off → warm placeholder ("hasn't shared upcoming
//     opportunities right now ✦") — never "No events".
//   • Empty → warm placeholder ("hasn't accepted any upcoming
//     shifts yet ✦") — never "Nothing here".
//   • Soft city/area location only — never full address.
//   • No map view, no "directions to venue", no countdown.
import React from 'react';
import { Calendar, MapPin, SealCheck } from '@phosphor-icons/react';
import {
  PANEL_BORDER, TEXT, TEXT_MUTED, BLUE, ROSE, SAGE,
} from './palette';
import { Section, CalmPlaceholder } from './Section';

export const UpcomingOpportunitiesSection = ({ firstName, upcoming, accent = BLUE }) => (
  <Section
    eyebrow="✦ Upcoming"
    title="What's coming up next"
    testid="parent-dashboard-upcoming"
    accent={accent}
  >
    {!upcoming.enabled && (
      <CalmPlaceholder testid="parent-dashboard-upcoming-off">
        {firstName} hasn't shared upcoming opportunities right now ✦
      </CalmPlaceholder>
    )}
    {upcoming.enabled && upcoming.shifts.length === 0 && (
      <CalmPlaceholder testid="parent-dashboard-upcoming-empty">
        {firstName} hasn't accepted any upcoming shifts yet ✦ — they'll
        appear here once they do.
      </CalmPlaceholder>
    )}
    {upcoming.enabled && upcoming.shifts.map((u) => (
      <div
        key={u.opportunity_id}
        data-testid={`parent-dashboard-shift-${u.opportunity_id}`}
        style={{
          padding: 14, borderRadius: 14, marginTop: 10,
          background: 'rgba(255,255,255,0.025)', border: `1px solid ${PANEL_BORDER}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <p style={{ margin: 0, fontWeight: 700, fontSize: 15, color: TEXT, lineHeight: 1.35 }}>
            {u.title}
          </p>
          {u.pay && (
            <span style={{
              flexShrink: 0, padding: '3px 9px', borderRadius: 999,
              background: `${SAGE}14`, border: `1px solid ${SAGE}40`,
              fontSize: 11.5, fontWeight: 700, color: TEXT,
            }}>
              {u.pay}
            </span>
          )}
        </div>
        <p style={{
          margin: '6px 0 0', fontSize: 12.5, color: TEXT_MUTED,
          display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Calendar size={12} weight="fill" />
            {u.event_date}{u.start_time ? ` · ${u.start_time}` : ''}
          </span>
          {u.location_soft && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <MapPin size={12} weight="fill" />
              {u.location_soft}
            </span>
          )}
        </p>
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span style={{
            padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: TEXT,
            background: u.supplier_verified ? `${BLUE}1A` : 'rgba(255,255,255,0.04)',
            border: `1px solid ${u.supplier_verified ? BLUE + '55' : PANEL_BORDER}`,
            display: 'inline-flex', alignItems: 'center', gap: 5,
          }}>
            {u.supplier_verified && <SealCheck size={11} weight="fill" color={BLUE} />}
            {u.supplier_name}{u.supplier_verified ? ' · Verified' : ''}
          </span>
          {u.is_repeat_invite && (
            <span style={{
              padding: '4px 10px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: TEXT,
              background: `${ROSE}14`, border: `1px solid ${ROSE}40`,
            }}>
              Repeat invite ✦
            </span>
          )}
        </div>
      </div>
    ))}
  </Section>
);
